import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useRef } from "react";

import { AnalyticsPage } from "../../../components/dashboard/analytics";
import { useOneTimePageAnimation } from "@/components/motion";
import { deliverabilityQueryOptions } from "@/features/deliverability/queries";
import type { DeliverabilityRange } from "@/features/deliverability/server";
import { verifiedIdentityOptionsQueryOptions } from "@/features/identities/queries";

export const Route = createFileRoute("/dashboard/analytics/")({
  validateSearch: (search: Record<string, unknown>) => ({
    domainId: (search.domainId as string) || undefined,
    aliasId: (search.aliasId as string) || undefined,
    range: ((search.range as DeliverabilityRange) || "7d") as DeliverabilityRange,
  }),
  head: () => ({
    meta: [{ title: "Analytics - Bavimail" }],
  }),
  staleTime: 30_000,
  preloadStaleTime: 30_000,
  loaderDeps: ({ search }) => ({
    domainId: search.domainId,
    aliasId: search.aliasId,
    range: search.range,
  }),
  loader: async ({ deps, context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(
        deliverabilityQueryOptions({
          range: deps.range,
          domainId: deps.domainId,
          aliasId: deps.aliasId,
        }),
      ),
      context.queryClient.ensureQueryData(verifiedIdentityOptionsQueryOptions()),
    ]);
  },
  component: DashboardAnalyticsRoute,
});

function DashboardAnalyticsRoute() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { domainId, aliasId, range } = Route.useSearch();
  const deliverabilityAnimationStage = useOneTimePageAnimation("dashboard-deliverability-panels");
  const prewarmedTargetsRef = useRef(new Map<string, number>());
  const { data } = useSuspenseQuery(
    deliverabilityQueryOptions({
      range,
      domainId,
      aliasId,
    }),
  );
  const { data: identityOptions } = useSuspenseQuery(verifiedIdentityOptionsQueryOptions());

  function shouldPrewarm(key: string) {
    const now = Date.now();
    const previous = prewarmedTargetsRef.current.get(key);
    if (previous && now - previous < 3_000) {
      return false;
    }

    prewarmedTargetsRef.current.set(key, now);
    return true;
  }

  async function prewarmDeliverabilityTarget(next: {
    domainId: string | undefined;
    aliasId: string | undefined;
    range: DeliverabilityRange;
  }) {
    const key = JSON.stringify(next);
    if (!shouldPrewarm(key)) return;

    await Promise.all([
      router.preloadRoute({ to: "/dashboard/analytics", search: next }),
      queryClient.prefetchQuery(
        deliverabilityQueryOptions({
          range: next.range,
          domainId: next.domainId,
          aliasId: next.aliasId,
        }),
      ),
    ]);
  }

  return (
    <AnalyticsPage
      summary={data.summary}
      points={data.points}
      recent={data.recent}
      deliverabilityAnimationStage={deliverabilityAnimationStage}
      ranges={["24h", "7d", "30d"]}
      selectedRange={range}
      selectedDomainId={domainId}
      selectedAliasId={aliasId}
      domains={identityOptions.domains.map((domain) => ({ id: domain.id, domain: domain.domain }))}
      aliases={identityOptions.aliases.map((alias) => ({
        id: alias.id,
        email: alias.fullEmail,
        domainId: alias.domainId,
      }))}
      onSelectRange={(nextRange) => {
        void navigate({
          to: "/dashboard/analytics",
          search: { domainId, aliasId, range: nextRange },
        });
      }}
      onPrefetchRange={(nextRange: DeliverabilityRange) => {
        void prewarmDeliverabilityTarget({ domainId, aliasId, range: nextRange });
      }}
      onSelectDomain={(nextDomainId) => {
        const nextAliasId =
          aliasId &&
          nextDomainId &&
          identityOptions.aliases.some(
            (alias) => alias.id === aliasId && alias.domainId === nextDomainId,
          )
            ? aliasId
            : nextDomainId
              ? undefined
              : aliasId;
        void navigate({
          to: "/dashboard/analytics",
          search: { domainId: nextDomainId, aliasId: nextAliasId, range },
        });
      }}
      onPrefetchDomain={(nextDomainId: string | undefined) => {
        const nextAliasId =
          aliasId &&
          nextDomainId &&
          identityOptions.aliases.some(
            (alias) => alias.id === aliasId && alias.domainId === nextDomainId,
          )
            ? aliasId
            : nextDomainId
              ? undefined
              : aliasId;
        void prewarmDeliverabilityTarget({ domainId: nextDomainId, aliasId: nextAliasId, range });
      }}
      onSelectAlias={(nextAliasId) => {
        void navigate({
          to: "/dashboard/analytics",
          search: { domainId, aliasId: nextAliasId, range },
        });
      }}
      onPrefetchAlias={(nextAliasId: string | undefined) => {
        void prewarmDeliverabilityTarget({ domainId, aliasId: nextAliasId, range });
      }}
    />
  );
}
